import type { Component } from 'vue'
import { CircleCheck, Download, RefreshCw, TriangleAlert } from 'lucide-vue-next'

import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { Tablet } from '@/types/types'

export type TabletVersionBadge = {
  color: ChipColor
  icon: Component
  label: string
  tooltip: string | null
}

const platformLabels: Record<string, string> = {
  android: 'Android',
  ios: 'iOS'
}

export default function useTabletVersion(): {
  nativeVersion: (tablet: Tablet) => string | null
  otaUpdateId: (tablet: Tablet) => string | null
  platformLabel: (tablet: Tablet) => string
  versionLabel: (tablet: Tablet) => string | null
  versionBadge: (tablet: Tablet) => TabletVersionBadge | null
} {
  const { t } = useI18n()

  const prefix = 'pages.marketplace.tabletsPage.versionStatus'

  const nativeVersion = (tablet: Tablet): string | null => tablet.applicationVersion?.split('-')[0] ?? null

  const otaUpdateId = (tablet: Tablet): string | null => {
    const native = nativeVersion(tablet)
    if (!native || !tablet.applicationVersion) {
      return null
    }
    return tablet.applicationVersion.slice(native.length + 1) || null
  }

  const versionLabel = (tablet: Tablet): string | null => {
    const native = nativeVersion(tablet)
    if (!native) {
      return null
    }
    const ota = otaUpdateId(tablet)
    return ota ? `${native} (${ota})` : native
  }

  const platformLabel = (tablet: Tablet): string => {
    const platform = tablet.platformShort
    return platform ? (platformLabels[platform.toLowerCase()] ?? platform) : ''
  }

  const versionBadge = (tablet: Tablet): TabletVersionBadge | null => {
    switch (tablet.versionStatus) {
      case 'up_to_date':
        return {
          color: 'green',
          icon: CircleCheck,
          label: t(`${prefix}.upToDate`),
          tooltip: versionLabel(tablet)
        }
      case 'outdated_ota':
        return {
          color: 'orange',
          icon: RefreshCw,
          label: t(`${prefix}.outdatedOta`),
          tooltip: t(`${prefix}.outdatedOtaTooltip`)
        }
      case 'outdated_native':
        return {
          color: 'orange',
          icon: Download,
          label: t(`${prefix}.outdatedNative`, { version: tablet.latestApplicationVersion ?? '' }),
          tooltip: t(`${prefix}.outdatedNativeTooltip`)
        }
      case 'unsupported':
        return {
          color: 'red',
          icon: TriangleAlert,
          label: t(`${prefix}.unsupported`),
          tooltip: t(`${prefix}.unsupportedTooltip`)
        }
      default:
        return null
    }
  }

  return { nativeVersion, otaUpdateId, platformLabel, versionBadge, versionLabel }
}
